/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 調査詳細の「事前スキャン」パネル。起票時に保存した重複スキャン スナップショット（request.preScan）を折りたたみ可能に表示する。抽出キーワード + 類似 ops 依頼 + 類似 Backlog バグチケットを並べ、各行の「リンク」ボタンで関連チケットへ紐付ける（ops → reference_ops / bug → reference_bug）。既に紐付け済みの行はリンクを無効化する。結果はノイズになりうるので既定は折りたたみ。preScan が無ければ控えめに「事前スキャンなし」を出す。ops.requests.$id 本体から分離
 * @graph-connects OpsDetailPage [references] 依頼詳細ページ本体から事前スキャンパネルを分離
 * @graph-connects useAddOpsRelatedTicket [calls] 事前スキャン結果の行を関連チケットへ紐付ける（POST）を実行
 */
import { useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { opsDisplayNo } from '@cortex/ops-core';
import { StateBadge } from './OpsPrimitives.js';
import { useAddOpsRelatedTicket } from '../../../hooks/useOps.js';
import type { OpsRelatedTicketInput } from '../../../lib/api/ops.js';
import type {
  OpsRelatedTicket,
  OpsRequest,
  OpsSimilarBug,
  OpsSimilarItem,
} from '../../../types/ops.js';

/** (target, ref) が既に関連チケットに含まれているか判定する（重複リンク防止）。 @graph-connects none */
function alreadyLinked(
  linked: readonly OpsRelatedTicket[],
  target: 'ops' | 'backlog',
  ref: string,
): boolean {
  return linked.some((tk) => tk.target === target && tk.ref === ref);
}

/** 類似 ops / 類似バグ 1 行の共通ラッパ（本文 + リンクボタン）。 @graph-connects none */
function ScanRow({
  linked,
  input,
  hits,
  children,
  onLink,
  isLinking,
  t,
}: {
  linked: boolean;
  input: OpsRelatedTicketInput;
  hits: number;
  children: ReactNode;
  onLink: (input: OpsRelatedTicketInput) => void;
  isLinking: boolean;
  t: TFunction;
}): ReactNode {
  return (
    <div className="flex items-center gap-2 text-xs">
      <div className="flex min-w-0 flex-1 items-center gap-2">{children}</div>
      <span className="shrink-0 text-[10px] text-[var(--color-text-muted)]">
        {t('ops.detail.preScan.hits', { count: hits })}
      </span>
      {linked ? (
        <span className="shrink-0 text-[10px] text-[var(--color-text-muted)]">
          {t('ops.detail.preScan.linked')}
        </span>
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={isLinking}
          onClick={() => {
            onLink(input);
          }}
        >
          {t('ops.detail.preScan.link')}
        </Button>
      )}
    </div>
  );
}

/** OpsPreScanPanel の props。 */
export interface OpsPreScanPanelProps {
  /** 表示対象の依頼（id / preScan / relatedTickets を参照）。 */
  request: OpsRequest;
  /** 翻訳関数。 */
  t: TFunction;
}

/**
 * 事前スキャンパネル。preScan スナップショットを折りたたみ表示し、各行を関連チケットへ紐付ける。
 * preScan が無ければ控えめな「事前スキャンなし」だけを描画する。
 * @graph-connects none
 */
export function OpsPreScanPanel({ request, t }: OpsPreScanPanelProps): ReactNode {
  const [expanded, setExpanded] = useState(false);
  const addMutation = useAddOpsRelatedTicket();
  const { preScan } = request;

  const onLink = (input: OpsRelatedTicketInput): void => {
    addMutation.mutate({ id: request.id, input });
  };

  if (preScan === null) {
    return <p className="text-xs text-[var(--color-text-muted)]">{t('ops.detail.preScan.none')}</p>;
  }

  const linkOps = (item: OpsSimilarItem): OpsRelatedTicketInput => ({
    target: 'ops',
    kind: 'reference_ops',
    ref: item.id,
    title: item.title,
  });
  const linkBug = (bug: OpsSimilarBug): OpsRelatedTicketInput => ({
    target: 'backlog',
    kind: 'reference_bug',
    ref: bug.issueKey,
    url: bug.url,
    title: bug.summary,
  });

  return (
    <Card padding="sm">
      <button
        type="button"
        onClick={() => {
          setExpanded((prev) => !prev);
        }}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2 text-left"
      >
        <span className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('ops.detail.preScan.title')}
        </span>
        <span className="text-xs text-[var(--color-text-muted)]">
          {t('ops.detail.preScan.counts', {
            ops: preScan.ops.length,
            bugs: preScan.bugs.length,
          })}
        </span>
        <span className="ml-auto text-xs text-[var(--color-text-secondary)]">
          {expanded ? t('ops.detail.preScan.collapse') : t('ops.detail.preScan.expand')}
        </span>
      </button>

      {expanded && (
        <CardContent className="mt-2 space-y-3 px-0 pb-0">
          {preScan.keywords.length > 0 && (
            <p className="text-[11px] text-[var(--color-text-muted)]">
              {t('ops.detail.preScan.matchedOn', { keywords: preScan.keywords.join(' · ') })}
            </p>
          )}

          {preScan.bugs.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold text-[var(--color-text-muted)]">
                {t('ops.detail.preScan.bugsHeading', { count: preScan.bugs.length })}
              </div>
              <div className="space-y-1">
                {preScan.bugs.map((bug) => (
                  <ScanRow
                    key={bug.issueKey}
                    linked={alreadyLinked(request.relatedTickets, 'backlog', bug.issueKey)}
                    input={linkBug(bug)}
                    hits={bug.hits}
                    onLink={onLink}
                    isLinking={addMutation.isPending}
                    t={t}
                  >
                    <a
                      href={bug.url}
                      target="_blank"
                      rel="noreferrer"
                      className="shrink-0 font-semibold text-[var(--color-accent-primary)] hover:underline"
                    >
                      {bug.issueKey}
                    </a>
                    <span className="shrink-0 rounded bg-[var(--color-bg-hover)] px-1 text-[10px] text-[var(--color-text-muted)]">
                      {bug.status}
                    </span>
                    <span className="min-w-0 truncate text-[var(--color-text-secondary)]">
                      {bug.summary}
                    </span>
                  </ScanRow>
                ))}
              </div>
            </div>
          )}

          {preScan.ops.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold text-[var(--color-text-muted)]">
                {t('ops.detail.preScan.opsHeading', { count: preScan.ops.length })}
              </div>
              <div className="space-y-1">
                {preScan.ops.map((item) => (
                  <ScanRow
                    key={item.id}
                    linked={alreadyLinked(request.relatedTickets, 'ops', item.id)}
                    input={linkOps(item)}
                    hits={item.hits}
                    onLink={onLink}
                    isLinking={addMutation.isPending}
                    t={t}
                  >
                    <span className="shrink-0 font-semibold text-[var(--color-accent-primary)]">
                      #{opsDisplayNo(item.id)}
                    </span>
                    <StateBadge state={item.state} />
                    <span className="min-w-0 truncate text-[var(--color-text-secondary)]">
                      {item.title}
                    </span>
                  </ScanRow>
                ))}
              </div>
            </div>
          )}

          {preScan.bugs.length === 0 && preScan.ops.length === 0 && (
            <p className="text-xs text-[var(--color-text-secondary)]">
              {t('ops.detail.preScan.empty')}
            </p>
          )}

          {addMutation.isError && (
            <p className="text-xs text-[var(--color-error)]">
              {t('ops.detail.preScan.linkFailed')}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
